'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useSetScratched } from '@/app/(main)/checkout/_hooks/useSetScratched';
import { usePrizeModalQueue } from '@/components';
import type { PrizeModalQueueItem } from '@/components/PrizeModal/components/usePrizeModalQueue';
import { toast } from '@/libs';
import { useDrawerStore, useSpinAllStore } from '@/stores';
import type { CustomerDataWithDocument } from '@/stores/customerStore';
import type {
  ScratchedPrize,
  SetScratchedParams,
  SetScratchedRequest,
  SetScratchedResponse,
} from '@/types/models/SetScratched';
import { logger, parseDigits } from '@/utils';
import { addDDIToPhone, formatCurrency } from '@/utils/formatters';

export type SpinAllStatus = 'success' | 'empty' | 'error';

export type SpinAllOutcome = {
  status: SpinAllStatus;
  prizes: ScratchedPrize[];
  totalPrizeValue: number;
  scratchedCount: number;
};

type UseSpinAllParams = {
  raffleId: string | undefined;
  customerData: CustomerDataWithDocument | null | undefined;
  pendingSpins: number;
  canSpinAll: boolean;
  capitalizer?: string | null;
  maxPrizeValue?: number;
  runBatchAnimation: (targetPrizeValue?: number) => Promise<void>;
  stopAutoSpin: () => void;
  onBatchSettled: (outcome: SpinAllOutcome) => void;
};

type UseSpinAllReturn = {
  isSpinningAll: boolean;
  spinAllCount: number;
  isPrizeQueueDraining: boolean;
  spinAll: () => Promise<void>;
};

const MAX_TARGET_WAIT_MS = 8000;
const REQUEST_TIMEOUT_MS = 30000;

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T | undefined> =>
  Promise.race([
    promise,
    new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), ms)),
  ]);

export const useSpinAll = ({
  raffleId,
  customerData,
  pendingSpins,
  canSpinAll,
  capitalizer,
  maxPrizeValue,
  runBatchAnimation,
  stopAutoSpin,
  onBatchSettled,
}: UseSpinAllParams): UseSpinAllReturn => {
  const { setTicketsAsScratched } = useSetScratched();
  const { enqueuePrizes, isDraining } = usePrizeModalQueue();
  const closeCurrentDrawer = useDrawerStore((state) => state.closeCurrentDrawer);
  const setSpinAllButton = useSpinAllStore((state) => state.setSpinAllButton);
  const resetSpinAllButton = useSpinAllStore((state) => state.resetSpinAllButton);

  const [isSpinningAll, setIsSpinningAll] = useState(false);
  const [spinAllCount, setSpinAllCount] = useState(0);
  const isSpinningAllRef = useRef(false);

  // O footerContent do drawer congela o onClick no momento da abertura, entao a
  // guarda precisa ler o estado corrente por ref, nao pela closure do render.
  const canSpinAllRef = useRef(canSpinAll);
  const pendingSpinsRef = useRef(pendingSpins);

  useEffect(() => {
    canSpinAllRef.current = canSpinAll;
    pendingSpinsRef.current = pendingSpins;
  }, [canSpinAll, pendingSpins]);

  const toQueueItems = useCallback(
    (prizes: ScratchedPrize[]): PrizeModalQueueItem[] => {
      const highest = maxPrizeValue ?? Math.max(...prizes.map((p) => p.prizeValue), 0);

      return prizes.map((prize) => ({
        id: prize.id,
        prizeLabel: formatCurrency(prize.prizeValue),
        prizeValue: prize.prizeValue,
        isBigGain: prize.prizeValue > 0 && prize.prizeValue === highest,
      }));
    },
    [maxPrizeValue],
  );

  const spinAll = useCallback(async () => {
    if (!canSpinAllRef.current || isSpinningAllRef.current || !raffleId) return;

    const batchSize = pendingSpinsRef.current;

    isSpinningAllRef.current = true;
    setIsSpinningAll(true);
    setSpinAllCount(batchSize);

    stopAutoSpin();
    closeCurrentDrawer();

    const params: SetScratchedParams = {
      cpf: parseDigits(customerData?.document || ''),
      phone: addDDIToPhone(parseDigits(customerData?.phone || '')),
    };
    const payload: SetScratchedRequest = {
      scratchAllRaffleTickets: true,
      raffleId,
    };

    let response: SetScratchedResponse | undefined;

    try {
      const requestPromise = setTicketsAsScratched(params, payload);

      const early = await withTimeout(
        requestPromise.catch(() => undefined),
        MAX_TARGET_WAIT_MS,
      );

      const topPrizeValue = early?.prizes?.length
        ? Math.max(...early.prizes.map((prize) => prize.prizeValue))
        : undefined;

      const [settled] = await Promise.all([
        early ?? withTimeout(requestPromise, REQUEST_TIMEOUT_MS),
        runBatchAnimation(topPrizeValue),
      ]);
      response = settled;
    } catch (error) {
      logger.error('Error spinning all tickets:', error);
      response = undefined;
    }

    isSpinningAllRef.current = false;
    setIsSpinningAll(false);

    if (!response) {
      toast.error('Não foi possível processar seus giros. Tente novamente.');
      onBatchSettled({
        status: 'error',
        prizes: [],
        totalPrizeValue: 0,
        scratchedCount: 0,
      });
      return;
    }

    const prizes = response.prizes ?? [];
    const outcome: SpinAllOutcome = {
      status: prizes.length > 0 ? 'success' : 'empty',
      prizes,
      totalPrizeValue: response.totalPrizeValue ?? 0,
      scratchedCount: response.scratchedCount ?? batchSize,
    };

    if (outcome.status === 'success') {
      enqueuePrizes(toQueueItems(prizes), {
        capitalizer,
        onDrained: () => onBatchSettled(outcome),
      });
      return;
    }

    onBatchSettled(outcome);
  }, [
    raffleId,
    stopAutoSpin,
    closeCurrentDrawer,
    customerData,
    setTicketsAsScratched,
    runBatchAnimation,
    enqueuePrizes,
    toQueueItems,
    capitalizer,
    onBatchSettled,
  ]);

  useEffect(() => {
    setSpinAllButton({
      label: `GIRAR TODOS (${pendingSpins})`,
      disabled: !canSpinAll || isSpinningAll,
      onClick: spinAll,
    });
  }, [pendingSpins, canSpinAll, isSpinningAll, spinAll, setSpinAllButton]);

  useEffect(() => () => resetSpinAllButton(), [resetSpinAllButton]);

  return {
    isSpinningAll,
    spinAllCount,
    isPrizeQueueDraining: isDraining,
    spinAll,
  };
};
